import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Divider,
  Grid,
  Heading,
  HStack,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  VStack,
} from "@chakra-ui/react";
import { CheckIcon, ChevronDownIcon, ChevronRightIcon, RepeatIcon } from "@chakra-ui/icons";
import { usePlacingBook } from "../../hooks/usePlacingBook";
import { usePartyNames } from "../../hooks/usePartyNames";
import { STAGES, STAGE_LABELS, marketCell, marketParties, marketTotal } from "./placingBook";
import { MarketBar, NextChip, STAGE_COLOURS } from "./PlacingSummaryParts";
import PlacementWorkspace from "./PlacementWorkspace";

const CELL_COLOURS = {
  signed: "green.100",
  written: "teal.100",
  quoted: "blue.100",
  requested: "gray.100",
  declined: "red.100",
  interested: "teal.50",
  muted: "gray.50",
};

const STAGE_BG = { submission: "blue.50", quotation: "teal.50", order: "cyan.50", bound: "green.50" };

// The short state on each stage chip of a placement card
const chipState = (s, stage) => {
  const i = STAGES.indexOf(stage);
  if (i > s.stageIndex) return null;
  if (stage === "submission") {
    if (!s.submission) return "not started";
    if (s.submission.status !== "shared") return "draft";
    const sent = s.leads.filter((l) => l.status !== "selected").length;
    return sent ? `shared · ${sent} lead${sent === 1 ? "" : "s"}` : "shared";
  }
  if (stage === "quotation") {
    const quoted = s.quoteRequests.filter((q) => ["quoted", "nominated"].includes(q.status)).length;
    const declined = s.quoteRequests.filter((q) => q.status === "declined").length;
    return s.quoteRequests.length ? `${quoted} quoted${declined ? ` · ${declined} declined` : ""}` : "";
  }
  if (stage === "order") return `${new Set(s.offers.map((o) => o.sender?.partyId)).size} written`;
  return s.signedDown ? `${s.signedLineAdvices.length} advices` : "";
};

const StageChip = ({ summary, stage }) => {
  const i = STAGES.indexOf(stage);
  const done = i < summary.stageIndex || (i === 3 && summary.stageIndex === 3);
  const current = i === summary.stageIndex && !done;
  const state = chipState(summary, stage);
  return (
    <HStack
      spacing={1}
      px={2.5}
      py={0.5}
      borderRadius="full"
      borderWidth={current ? "2px" : "1px"}
      borderColor={current ? "teal.500" : done ? "green.200" : "gray.200"}
      bg={done ? "green.50" : "white"}
      fontSize="xs">
      {done ? <CheckIcon boxSize={2.5} color="green.600" /> : <Box boxSize={2} borderRadius="full" borderWidth="1.5px" borderColor={current ? "teal.500" : "gray.400"} bg={current ? "teal.500" : undefined} />}
      <Text fontWeight="bold">{STAGE_LABELS[stage].toUpperCase()}</Text>
      {state && <Text color="gray.600">{state}</Text>}
    </HStack>
  );
};

/** A deal's placements: programme overview, a card per placement, and the market across them. */
const DealPlacementsTab = ({ deal, productCode, documentNames = {} }) => {
  const navigate = useNavigate();
  // Refreshes every minute while the page is open, like the placing portal
  const { loading, refreshing, lastUpdated, summaries: all, reload } = usePlacingBook();
  const [openId, setOpenIdState] = useState(null);
  // The market is open on its own, and folds (below the detail) while a placement's detail is open
  const [marketOpen, setMarketOpen] = useState(true);
  const setOpenId = (id) => {
    setOpenIdState(id);
    setMarketOpen(!id);
  };

  const summaries = all.filter((s) => s.placement.dealId === deal?._id);
  const parties = marketParties(summaries);
  const names = usePartyNames(parties);

  const editorLink = (documentId) => `/editor/${productCode}/${deal?._id}?doc=${documentId}`;
  const placedDocs = summaries.map((s) => s.placement.documentId);
  const unplaced = (deal?.documents || []).filter((d) => d.documentRepositoryId && !placedDocs.includes(d.documentRepositoryId));
  const docName = (id) => documentNames[id]?.name || "Untitled document";
  const bound = summaries.filter((s) => s.isBound).length;

  if (loading) return <Spinner />;

  const market = (
    summaries.length > 0 && (
        <Box borderWidth="1px" borderRadius="md" bg="whiteAlpha.800" px={4} py={3} overflowX="auto">
          <HStack justify="space-between" cursor="pointer" onClick={() => setMarketOpen(!marketOpen)} role="button" aria-expanded={marketOpen}>
            <HStack spacing={2}>
              {marketOpen ? <ChevronDownIcon /> : <ChevronRightIcon />}
              <Heading size="sm">Market across the programme</Heading>
              {!marketOpen && (
                <Text fontSize="sm" color="gray.500">
                  {parties.length} carrier{parties.length === 1 ? "" : "s"} across {summaries.length} placement{summaries.length === 1 ? "" : "s"} · {summaries.map(marketTotal).join(" / ")}
                </Text>
              )}
            </HStack>
            {marketOpen && <Text fontSize="xs" color="gray.500">Each carrier's position on each placement · L = lead · ⚓ = line to stand</Text>}
          </HStack>
          {!marketOpen ? null : parties.length === 0 ? (
            <Text fontSize="sm" color="gray.500">No carriers approached yet.</Text>
          ) : (
            <Table size="sm" mt={2} sx={{ "td, th": { py: 1.5 } }}>
              <Thead>
                <Tr>
                  <Th>Carrier</Th>
                  {summaries.map((s) => (
                    <Th key={s.placement._id}>{s.placement.name || "Placement"} · {STAGE_LABELS[s.stage]}</Th>
                  ))}
                </Tr>
              </Thead>
              <Tbody>
                {parties.map((partyId) => (
                  <Tr key={partyId}>
                    <Td fontWeight="semibold">{names[partyId] || "Loading…"}</Td>
                    {summaries.map((s) => {
                      const cell = marketCell(s, partyId);
                      return (
                        <Td key={s.placement._id}>
                          {cell ? (
                            <Box display="inline-block" px={2} py={0.5} borderRadius="sm" bg={CELL_COLOURS[cell.tone]} fontSize="xs">
                              {cell.isLead && "L · "}
                              {cell.text}
                              {cell.lineToStand && " ⚓"}
                              {cell.moreRequests > 0 && (
                                <Text as="span" color="gray.500"> · +{cell.moreRequests} more request(s)</Text>
                              )}
                            </Box>
                          ) : (
                            <Text color="gray.400">—</Text>
                          )}
                        </Td>
                      );
                    })}
                  </Tr>
                ))}
                <Tr>
                  <Td fontWeight="semibold">Total</Td>
                  {summaries.map((s) => (
                    <Td key={s.placement._id} fontFamily="mono" fontSize="xs" fontWeight="semibold">{marketTotal(s)}</Td>
                  ))}
                </Tr>
              </Tbody>
            </Table>
          )}
        </Box>
      )
  );

  return (
    <VStack align="stretch" spacing={4}>
      <Grid templateColumns={{ base: "1fr", lg: "220px 1fr" }} gap={4}>
        <Box borderWidth="1px" borderRadius="md" bg="whiteAlpha.800" p={4}>
          <Heading size="sm">Programme</Heading>
          <Text fontSize="xs" color="gray.500" mb={3}>
            {bound} of {summaries.length} placement(s) signed down
          </Text>
          <VStack align="stretch" spacing={2}>
            {summaries.map((s) => (
              <Box
                key={s.placement._id}
                borderWidth="2px"
                borderColor={STAGE_COLOURS[s.stage]}
                bg={STAGE_BG[s.stage]}
                borderRadius="md"
                p={2}
                minH="64px"
                cursor="pointer"
                onClick={() => setOpenId(s.placement._id)}>
                <HStack justify="space-between" fontSize="xs">
                  <Text fontWeight="semibold">{s.placement.name || "Placement"}</Text>
                  <Text color="teal.700">{STAGE_LABELS[s.stage]}</Text>
                </HStack>
                <Text fontSize="xs" color="gray.600" mt={3} textAlign="right">
                  {s.market ? `${s.market.percent}% ${s.market.label}` : s.stageSummary}
                </Text>
              </Box>
            ))}
            {summaries.length === 0 && <Text fontSize="sm" color="gray.500">No placements yet.</Text>}
          </VStack>
          <Text fontSize="xs" color="gray.500" mt={3}>
            Colour shows placing stage. Layer limits aren't recorded yet, so every placement is the same height.
          </Text>
        </Box>

        <Box>
          <HStack justify="space-between" mb={3}>
            <HStack spacing={3}>
              <Heading size="sm">Placements on this deal</Heading>
              <Tooltip label="Refreshes every minute while this page is open" hasArrow>
                <Text fontSize="xs" color="gray.500">
                  Updated {lastUpdated ? lastUpdated.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : ""}
                </Text>
              </Tooltip>
              <IconButton size="xs" variant="ghost" aria-label="Refresh now" icon={<RepeatIcon />} onClick={reload} isLoading={refreshing} />
            </HStack>
            {unplaced.length ? (
              <Menu>
                <MenuButton as={Button} size="sm" colorScheme="blue">+ New placement from a document</MenuButton>
                <MenuList>
                  {unplaced.map((d) => (
                    <MenuItem key={d.id || d.documentRepositoryId} onClick={() => navigate(editorLink(d.documentRepositoryId))}>
                      {docName(d.documentRepositoryId)}
                    </MenuItem>
                  ))}
                </MenuList>
              </Menu>
            ) : (
              <Tooltip label="Add a document on the Documents tab first" hasArrow>
                <Button size="sm" colorScheme="blue" isDisabled>+ New placement from a document</Button>
              </Tooltip>
            )}
          </HStack>

          <VStack align="stretch" spacing={2}>
            {summaries.map((s) => {
              const isOpen = openId === s.placement._id;
              const doc = docName(s.placement.documentId);
              return (
                <Grid
                  key={s.placement._id}
                  templateColumns={{ base: "1fr", xl: "minmax(160px, 1fr) auto auto auto" }}
                  gap={3}
                  alignItems="center"
                  borderWidth="1px"
                  borderLeftWidth="4px"
                  borderLeftColor={STAGE_COLOURS[s.stage]}
                  borderColor={isOpen ? "blue.300" : undefined}
                  borderRadius="md"
                  bg={isOpen ? "blue.50" : "whiteAlpha.800"}
                  px={3}
                  py={2}>
                  <Tooltip label={`Document ${doc === "Untitled document" ? s.placement.name : doc}`} hasArrow openDelay={300}>
                    <Text fontWeight="semibold" noOfLines={1}>{s.placement.name || "Placement"}</Text>
                  </Tooltip>
                  <HStack spacing={1.5} flexWrap="wrap" rowGap={1}>
                    {STAGES.map((st) => <StageChip key={st} summary={s} stage={st} />)}
                  </HStack>
                  <Box w="130px">{s.market ? <MarketBar market={s.market} width="70px" /> : <Text fontSize="xs" color="gray.400">no lines yet</Text>}</Box>
                  <HStack spacing={2} justify="flex-end">
                    <NextChip next={s.next} />
                    <Button size="xs" variant="link" colorScheme="blue" onClick={() => navigate(editorLink(s.placement.documentId))}>Open document</Button>
                    <Button size="xs" colorScheme="blue" variant={isOpen ? "solid" : "outline"} onClick={() => setOpenId(isOpen ? null : s.placement._id)}>
                      {isOpen ? "Hide" : "Details"}
                    </Button>
                  </HStack>
                </Grid>
              );
            })}
            {unplaced.map((d) => (
              <HStack key={d.id || d.documentRepositoryId} borderWidth="1px" borderStyle="dashed" borderRadius="md" bg="whiteAlpha.800" p={3} justify="space-between">
                <Text fontSize="sm">
                  <Text as="span" fontWeight="semibold">{docName(d.documentRepositoryId)}</Text>
                  <Text as="span" color="gray.500"> · document {d.status || "draft"}, not yet in placing</Text>
                </Text>
                <Button size="xs" variant="outline" onClick={() => navigate(editorLink(d.documentRepositoryId))}>Create submission</Button>
              </HStack>
            ))}
          </VStack>
        </Box>
      </Grid>

      {!openId && market}

      {openId && (
        <>
          <Divider />
          <PlacementWorkspace key={openId} placementId={openId} editorLink={editorLink} showDealLink={false} />
          {market}
        </>
      )}
    </VStack>
  );
};

export default DealPlacementsTab;
